/* global dataLayer */
import { Suspense, lazy, useEffect } from 'react';

import {
  useSoyuzData,
  useSoyuzExperiment,
  useSoyuzFlag
} from '@spotahome/soyuz/client';
import { RESULTS_PER_PAGE_48 } from '@spotahome/ui-library/src/utils/searchResults';
import {
  ToastContainer,
  ThemeProvider,
  CookiesProvider,
  UserInfoProvider,
  HotspotsProvider,
  TenantTypeProvider,
  DeviceProvider,
  GoogleOneTap,
  AppBanner,
  CitiesProvider,
  cityToggleConfig
} from '@spotahome/ui-library';
import JsonLdHomecardListCarousel from '@spotahome/ui-library/src/tenant/components/JsonLdHomecardListCarousel';
import { useCoreWebVitals } from '@spotahome/ui-library/src/hooks/useCoreWebVitals';
import usePageLoadTime from '@spotahome/ui-library/src/hooks/usePageLoadTime';
import ZohoChatbot from '@spotahome/ui-library/src/tenant/components/ZohoChatbot/ZohoChatbot';

import { useHistory } from 'react-router-dom';
import Metrics from '@spotahome/soyuz-tracking';

import { configureMetricsActions } from '@spotahome/marketplace-common/src/graphql/mutations/tenantActions';
import { getNumberActiveBanners } from '@spotahome/marketplace-common/src/components/HomeCardBanner/config';

import AdUrlService from '@spotahome/marketplace-common/src/utils/ad-url-service';

import InformationToastWrapper from '@spotahome/marketplace-common/src/components/InformationToast/index';
import ListingUnavailableModal from '@spotahome/marketplace-common/src/components/ListingUnavailableModal';
import OptInModalWrapper from '@spotahome/marketplace-common/src/components/OptInModalWrapper';
import SahContainer from '@spotahome/marketplace-common/src/components/SahContainer';

import CurrencyFormatter from '@spotahome/marketplace-common/src/utils/CurrencyFormatter';
import { isSeoMode } from '@spotahome/marketplace-common/src/utils/seo-mode';

import { HighlightMarkersProvider } from '@spotahome/marketplace-common/src/context/HighlightMarkersContext';

import '@spotahome/marketplace-common/src/scss/init.scss';

import NoSSR from 'react-no-ssr';

import { FiltersProvider } from '@spotahome/ui-library/src/tenant/context/FiltersContext';
import { FacetSearchProvider } from '@spotahome/ui-library/src/tenant/context/FacetSearchContext';

import NotFound from '@spotahome/marketplace-common/src/components/NotFound';

import Search from '@spotahome/marketplace-common/src/components/Search';

import { AvailabilitiesStatisticsProvider } from './context/AvailabilitiesStatisticsContext';
import { PricesStatisticsProvider } from './context/PricesStatisticsContext';
import { TenantProfileStatisticsProvider } from './context/TenantProfileStatisticsContext';

import { UserInteractionStatisticsProvider } from './context/UserInteractionStatistics';
import ChartsSection from './components/Charts/ChartsSection';

const LoginModal = lazy(() =>
  import('@spotahome/ui-library/src/tenant/components/LoginSignupModalWrapper')
);

const SearchPage = () => {
  const { [SearchPage.NAME]: requestedData } = useSoyuzData();
  const {
    error,
    city,
    maxPriceInCity,
    initialFiltersFromQuery,
    initialMarkerList = [],
    initialHomecards = {},
    isUserLogged,
    numberOfMarkers,
    areas,
    metadata,
    showUnavailableModal,
    locationString,
    locationCoordinates,
    deviceType,
    deviceViewType,
    initialCookie,
    preloadedHomecards,
    cityList,
    isSafari,
    internalLinking,
    seoSnippet,
    userHasFavorites,
    tenantType,
    sortingAlgorithm,
    mapProviderVariant,
    statistics
  } = requestedData;

  // On the agentic-search variant the agentic entry FAB owns the
  // bottom-right slot, so hide the Spotty Cat (Zoho) chatbot here. Listing
  // pages keep it.
  const showAgenticSearch =
    useSoyuzExperiment('AgenticSearch').get('behaviour') === 'variant';
  const countryCode = city?.country?.countryIsoCode;
  const currency = new CurrencyFormatter(city?.currencyIsoCode);
  const IS_SEO_MODE = isSeoMode(initialFiltersFromQuery);
  const history = useHistory();
  const updateUrlOnFilterChangeIsOn = useSoyuzFlag(
    'UpdateUrlOnFilterChange'
  ).isOn();

  useCoreWebVitals();
  usePageLoadTime();

  useEffect(() => {
    configureMetricsActions();

    Metrics.actions.sendActionEvent(Metrics.actions.VIEW_SEARCH, {
      cityId: city?.id
    });
  }, [city?.id]);

  useEffect(() => {
    const { userId } = requestedData;
    if (userId && typeof dataLayer !== 'undefined') {
      dataLayer.push({
        userId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error === 'notFound') {
    return (
      <NotFound
        initialCookie={initialCookie}
        deviceType={deviceType}
        deviceViewType={deviceViewType}
        source="search"
      />
    );
  }

  const mapCenter =
    typeof window !== 'undefined' &&
    AdUrlService.getQueryParamValue('mapCenter');

  const shouldUpdateUrlOnFiltersChange = !(
    cityToggleConfig(city?.id).shouldNotUpdateUrlOnFiltersChange &&
    updateUrlOnFilterChangeIsOn
  );

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <SahContainer>
          <AppBanner source="search" deviceType={deviceType} />
          <ToastContainer />
          {!isUserLogged && <GoogleOneTap />}
          {showUnavailableModal && <ListingUnavailableModal />}
          <InformationToastWrapper city={city?.id} />
          <UserInfoProvider>
            <CitiesProvider initialCityInfo={city} skipAllCities>
              <AvailabilitiesStatisticsProvider
                cityId={city?.id}
                propertyType={initialFiltersFromQuery['type[]']?.[0] || null}
                availabilitiesStatistics={
                  statistics?.cityAvailabilitiesStatistics || []
                }
              >
                <PricesStatisticsProvider
                  cityId={city?.id}
                  propertyType={initialFiltersFromQuery['type[]']?.[0] || null}
                  pricesStatistics={statistics?.cityPricesStatistics || []}
                  currencyIsoCode={currency?.isoCode}
                >
                  <TenantProfileStatisticsProvider
                    cityId={city?.id}
                    tenantProfileStatistics={
                      statistics?.cityTenantStatistics || []
                    }
                  >
                    <UserInteractionStatisticsProvider
                      userInteractionStatistics={
                        statistics?.cityUserInteractionStatistics || []
                      }
                    >
                      <HighlightMarkersProvider>
                        <HotspotsProvider>
                          <TenantTypeProvider tenantType={tenantType}>
                            <DeviceProvider deviceType={deviceType}>
                              {!showAgenticSearch && <ZohoChatbot />}
                              <FiltersProvider
                                initialFilters={initialFiltersFromQuery}
                                cityId={city?.id}
                                locationString={locationString}
                                shouldUpdateUrl={shouldUpdateUrlOnFiltersChange}
                              >
                                <FacetSearchProvider
                                  cityId={city?.id}
                                  initialFilters={initialFiltersFromQuery}
                                >
                                  <Search
                                    locationString={locationString}
                                    locationCoordinates={locationCoordinates}
                                    history={history}
                                    city={city}
                                    countryCode={countryCode}
                                    currency={currency}
                                    maxPriceInCity={maxPriceInCity}
                                    seoMode={IS_SEO_MODE}
                                    seoContent={metadata?.content}
                                    initialMarkerList={initialMarkerList}
                                    initialHomecards={initialHomecards}
                                    numberOfMarkers={numberOfMarkers}
                                    sortingAlgorithm={sortingAlgorithm}
                                    mapProviderVariant={mapProviderVariant}
                                    itemsPerPage={
                                      RESULTS_PER_PAGE_48 -
                                      getNumberActiveBanners({
                                        cityId: city?.id
                                      })
                                    }
                                    areas={areas}
                                    isUserLogged={isUserLogged}
                                    map={initialFiltersFromQuery.map === 'true'}
                                    mapCenter={mapCenter}
                                    preloadedHomecards={preloadedHomecards}
                                    cityList={cityList}
                                    isSafari={isSafari}
                                    internalLinking={
                                      internalLinking.searchInternalLinking
                                    }
                                    seoSnippet={seoSnippet}
                                    deviceType={deviceType}
                                    userHasFavorites={userHasFavorites}
                                    renderCharts={({ location, isMobile }) => (
                                      <ChartsSection
                                        location={location}
                                        isMobile={isMobile}
                                      />
                                    )}
                                  />
                                </FacetSearchProvider>
                              </FiltersProvider>
                            </DeviceProvider>
                          </TenantTypeProvider>
                        </HotspotsProvider>
                      </HighlightMarkersProvider>
                    </UserInteractionStatisticsProvider>
                  </TenantProfileStatisticsProvider>
                </PricesStatisticsProvider>
              </AvailabilitiesStatisticsProvider>
            </CitiesProvider>
            <NoSSR>
              <Suspense fallback={null}>
                <LoginModal deviceViewType={deviceViewType} />
              </Suspense>
            </NoSSR>
          </UserInfoProvider>
          <OptInModalWrapper />
          <JsonLdHomecardListCarousel
            homecards={initialMarkerList
              .map(marker => initialHomecards[marker.id])
              .filter(Boolean)}
          />
        </SahContainer>
      </ThemeProvider>
    </CookiesProvider>
  );
};

SearchPage.NAME = 'SearchPage';

export default SearchPage;
